import type { HlcClock } from '../../domain/hlc';
import { encodeHlc } from '../../domain/hlc';
import { uuidv7 } from '../../domain/ids';
import type { Synced } from '../../domain/model';
import { allValues, type Row, type SqlDriver, type SqlValue, type Statement } from '../db/driver';

/**
 * Generic persistence for synced entities. One spec per entity maps its
 * camelCase fields to snake_case columns (with codecs for booleans and
 * JSON). Every write goes out as a batch of statements: the row change
 * plus its sync_outbox record, so a write and its sync intent can never
 * diverge. Deletes are tombstones.
 */
export interface StoreContext {
  db: SqlDriver;
  clock: HlcClock;
  now: () => number;
  /**
   * Budgets that take no local writes now: a shared budget whose owner's
   * Premium ended is read-only for everyone until it is renewed (set by
   * sync; see sync/readOnly.ts). Every write goes through the outbox, so
   * this one check covers all of them; pulled rows never do.
   */
  readOnly?: (spaceId: string) => boolean;
}

/** A write to a read-only budget (see StoreContext.readOnly). Screens say why before it can happen; this is the backstop. */
export class ReadOnlyError extends Error {
  constructor(readonly spaceId: string) {
    super(`budget ${spaceId} is read-only`);
    this.name = 'ReadOnlyError';
  }
}

interface Codec {
  toDb(v: unknown): SqlValue;
  fromDb(v: SqlValue): unknown;
}

export const bool: Codec = {
  toDb: (v) => (v ? 1 : 0),
  fromDb: (v) => v === 1 || v === '1',
};

export const json: Codec = {
  toDb: (v) => JSON.stringify(v ?? null),
  fromDb: (v) => (typeof v === 'string' ? JSON.parse(v) : null),
};

const plain: Codec = { toDb: (v) => (v === undefined ? null : (v as SqlValue)), fromDb: (v) => v };

export type FieldMap<E> = { [K in Exclude<keyof E, keyof Synced>]-?: string | [string, Codec] };

export interface EntitySpec<E extends Synced> {
  table: string;
  fields: FieldMap<E>;
  /** Field holding the budget space id, for sync routing. */
  spaceField?: keyof E;
  /**
   * Fields that only make sense together (whose it is + its owner, type +
   * whether it counts). A change to one goes out with the others, so the
   * server's per-field merge keeps each pair from a single write.
   */
  together?: (keyof E)[][];
  /** Puts right a row that arrives from the server with fields that disagree (older apps, earlier merges). */
  normalize?: (fields: Record<string, unknown>) => Record<string, unknown>;
}

const SYNCED_COLUMNS: [keyof Synced, string][] = [
  ['id', 'id'],
  ['createdAt', 'created_at'],
  ['updatedHlc', 'updated_hlc'],
  ['deletedAt', 'deleted_at'],
];

function columnsOf<E extends Synced>(spec: EntitySpec<E>): [keyof E, string, Codec][] {
  const own = Object.entries(spec.fields).map(([k, v]) => {
    const [col, codec] = Array.isArray(v) ? (v as [string, Codec]) : [v as string, plain];
    return [k as keyof E, col, codec] as [keyof E, string, Codec];
  });
  return [...SYNCED_COLUMNS.map(([k, c]) => [k as keyof E, c, plain] as [keyof E, string, Codec]), ...own];
}

export type NewEntity<E extends Synced> = Omit<E, keyof Synced> & { id?: string };
export type Patch<E extends Synced> = Partial<Omit<E, keyof Synced>>;

export interface ListOptions {
  includeDeleted?: boolean;
  orderBy?: string;
}

export class EntityTable<E extends Synced> {
  private readonly cols: [keyof E, string, Codec][];
  private readonly byField: Map<keyof E, [string, Codec]>;

  constructor(
    readonly ctx: StoreContext,
    readonly spec: EntitySpec<E>,
  ) {
    this.cols = columnsOf(spec);
    this.byField = new Map(this.cols.map(([k, c, codec]) => [k, [c, codec]]));
  }

  column(field: keyof E): string {
    const c = this.byField.get(field);
    if (!c) throw new Error(`${this.spec.table}: unknown field ${String(field)}`);
    return c[0];
  }

  /** Rows of this table from any query selecting its columns (e.g. SELECT * … WHERE …), sent across packed (see allRows). */
  async select(sql: string, params: readonly SqlValue[] = []): Promise<E[]> {
    // Straight from the packed arrays to entities: one pass, no intermediate row objects (10,000 entries on a phone).
    const cols = this.cols;
    const n = cols.length;
    return (
      await allValues(
        this.ctx.db,
        sql,
        params,
        cols.map(([, c]) => c),
      )
    ).map((v) => {
      const e = {} as Record<string, unknown>;
      for (let i = 0; i < n; i++) {
        const [k, , codec] = cols[i]!;
        e[k as string] = codec.fromDb(v[i] ?? null);
      }
      return e as E;
    });
  }

  fromRow(row: Row): E {
    const e = {} as Record<string, unknown>;
    for (const [k, c, codec] of this.cols) e[k as string] = codec.fromDb(row[c] ?? null);
    return e as E;
  }

  private encode(field: keyof E, value: unknown): SqlValue {
    const [, codec] = this.byField.get(field)!;
    return codec.toDb(value);
  }

  private stamp() {
    return { hlc: encodeHlc(this.ctx.clock.tick()), now: this.ctx.now() };
  }

  /**
   * `prev`: the values this write replaced, as this device saw them. The
   * server compares them with what it holds: a difference means someone
   * else changed the entry meanwhile (a conflict, kept in the activity log).
   */
  private outbox(
    id: string,
    spaceId: string | null,
    op: 'upsert' | 'delete',
    hlc: string,
    fields: Record<string, unknown>,
    now: number,
    prev: Record<string, unknown> | null = null,
  ): Statement {
    if (spaceId && this.ctx.readOnly?.(spaceId)) throw new ReadOnlyError(spaceId);
    return {
      sql: 'INSERT INTO sync_outbox (table_name, row_id, space_id, op, hlc, fields, created_at, prev) VALUES (?, ?, ?, ?, ?, ?, ?, ?)',
      params: [this.spec.table, id, spaceId, op, hlc, JSON.stringify(fields), now, prev ? JSON.stringify(prev) : null],
    };
  }

  /** The row's own fields (not its bookkeeping), for `prev` of a delete. */
  private snapshot(e: E): Record<string, unknown> {
    return Object.fromEntries(this.cols.filter(([k]) => k !== 'updatedHlc' && k !== 'createdAt' && k !== 'id').map(([k]) => [k, e[k]]));
  }

  private spaceOf(e: Partial<E>): string | null {
    const f = this.spec.spaceField;
    return f ? ((e[f] as string | undefined) ?? null) : null;
  }

  /** Statements that insert a new row; returns the full entity as it will be stored. */
  prepareCreate(input: NewEntity<E>): { entity: E; statements: Statement[] } {
    const { hlc, now } = this.stamp();
    const entity = { ...input, id: input.id ?? uuidv7(now), createdAt: now, updatedHlc: hlc, deletedAt: null } as unknown as E;
    const cols = this.cols.map(([, c]) => c);
    const values = this.cols.map(([k]) => this.encode(k, entity[k]));
    const fields = Object.fromEntries(this.cols.filter(([k]) => k !== 'updatedHlc').map(([k]) => [k, entity[k]]));
    return {
      entity,
      statements: [
        { sql: `INSERT INTO ${this.spec.table} (${cols.join(', ')}) VALUES (${cols.map(() => '?').join(', ')})`, params: values },
        this.outbox(entity.id, this.spaceOf(entity), 'upsert', hlc, fields, now),
      ],
    };
  }

  prepareUpdate(current: E, patch: Patch<E>): { entity: E; statements: Statement[] } {
    const changed = (Object.keys(patch) as (keyof E)[]).filter((k) => patch[k as keyof Patch<E>] !== undefined && patch[k as keyof Patch<E>] !== current[k]);
    if (changed.length === 0) return { entity: current, statements: [] };
    for (const pair of this.spec.together ?? []) {
      if (pair.some((k) => changed.includes(k))) for (const k of pair) if (!changed.includes(k)) changed.push(k);
    }
    const { hlc, now } = this.stamp();
    const entity = { ...current, ...patch, updatedHlc: hlc } as E;
    const sets = changed.map((k) => `${this.column(k)} = ?`);
    const values = changed.map((k) => this.encode(k, entity[k]));
    return {
      entity,
      statements: [
        { sql: `UPDATE ${this.spec.table} SET ${sets.join(', ')}, updated_hlc = ? WHERE id = ?`, params: [...values, hlc, current.id] },
        this.outbox(
          current.id,
          this.spaceOf(entity),
          'upsert',
          hlc,
          Object.fromEntries(changed.map((k) => [k, entity[k]])),
          now,
          // What it was, and that it was not deleted (an edit to an entry deleted meanwhile is a conflict too).
          { ...Object.fromEntries(changed.map((k) => [k, current[k]])), deletedAt: current.deletedAt },
        ),
      ],
    };
  }

  /** `at` lets rows deleted together share one deletion moment (so they can be restored together). */
  prepareTombstone(current: E, deleted: boolean, at?: number): { entity: E; statements: Statement[] } {
    const { hlc, now } = this.stamp();
    const deletedAt = deleted ? (at ?? now) : null;
    const entity = { ...current, deletedAt, updatedHlc: hlc } as E;
    return {
      entity,
      statements: [
        { sql: `UPDATE ${this.spec.table} SET deleted_at = ?, updated_hlc = ? WHERE id = ?`, params: [deletedAt, hlc, current.id] },
        this.outbox(
          current.id,
          this.spaceOf(current),
          deleted ? 'delete' : 'upsert',
          hlc,
          { deletedAt },
          now,
          deleted ? this.snapshot(current) : { deletedAt: current.deletedAt },
        ),
      ],
    };
  }

  async get(id: string, { includeDeleted = false }: ListOptions = {}): Promise<E | undefined> {
    const rows = await this.select(`SELECT * FROM ${this.spec.table} WHERE id = ?${includeDeleted ? '' : ' AND deleted_at IS NULL'}`, [id]);
    return rows[0];
  }

  /** Equality filters on fields; `null` matches IS NULL. */
  async list(where: Partial<Record<keyof E, SqlValue | boolean>> = {}, { includeDeleted = false, orderBy }: ListOptions = {}): Promise<E[]> {
    const clauses: string[] = [];
    const params: SqlValue[] = [];
    for (const [k, v] of Object.entries(where) as [keyof E, SqlValue | boolean][]) {
      if (v === null) clauses.push(`${this.column(k)} IS NULL`);
      else {
        clauses.push(`${this.column(k)} = ?`);
        params.push(this.encode(k, v));
      }
    }
    if (!includeDeleted) clauses.push('deleted_at IS NULL');
    const sql = `SELECT * FROM ${this.spec.table}${clauses.length ? ` WHERE ${clauses.join(' AND ')}` : ''} ORDER BY ${orderBy ?? 'id'}`;
    return this.select(sql, params);
  }

  async require(id: string): Promise<E> {
    const e = await this.get(id);
    if (!e) throw new NotFoundError(this.spec.table, id);
    return e;
  }
}

export class NotFoundError extends Error {
  constructor(
    readonly table: string,
    readonly id: string,
  ) {
    super(`${table} ${id} not found`);
    this.name = 'NotFoundError';
  }
}

/** Plain CRUD over an EntityTable, with a validation hook. */
export class Repo<E extends Synced> {
  constructor(
    readonly table: EntityTable<E>,
    private readonly validate: (e: E) => void | Promise<void> = () => undefined,
  ) {}

  get db() {
    return this.table.ctx.db;
  }

  async create(input: NewEntity<E>): Promise<E> {
    const { entity, statements } = this.table.prepareCreate(input);
    await this.validate(entity);
    await this.db.batch(statements);
    return entity;
  }

  get(id: string, opts?: ListOptions) {
    return this.table.get(id, opts);
  }

  list(where?: Parameters<EntityTable<E>['list']>[0], opts?: ListOptions) {
    return this.table.list(where, opts);
  }

  async update(id: string, patch: Patch<E>): Promise<E> {
    const current = await this.table.require(id);
    const { entity, statements } = this.table.prepareUpdate(current, patch);
    if (statements.length === 0) return current;
    await this.validate(entity);
    await this.db.batch(statements);
    return entity;
  }

  /** Soft delete: the row stays as a tombstone so the deletion can sync and be undone. */
  async remove(id: string): Promise<E> {
    const current = await this.table.require(id);
    const { entity, statements } = this.table.prepareTombstone(current, true);
    await this.db.batch(statements);
    return entity;
  }

  async restore(id: string): Promise<E> {
    const current = await this.table.get(id, { includeDeleted: true });
    if (!current) throw new NotFoundError(this.table.spec.table, id);
    if (current.deletedAt === null) return current;
    const { entity, statements } = this.table.prepareTombstone(current, false);
    await this.validate(entity);
    await this.db.batch(statements);
    return entity;
  }
}

/**
 * A row as another device wrote it (sync pull): upsert the given fields
 * without touching the outbox, since the change is already on the server.
 * Fields this table does not know are ignored (a newer app version may
 * sync more than this one stores).
 */
export function remoteUpsertStatement<E extends Synced>(spec: EntitySpec<E>, incoming: Record<string, unknown>, updatedHlc: string): Statement | null {
  const fields = spec.normalize ? spec.normalize({ ...incoming }) : incoming;
  const known = columnsOf(spec).filter(([k]) => k !== 'updatedHlc' && Object.prototype.hasOwnProperty.call(fields, k));
  if (!known.some(([k]) => k === 'id') && typeof fields.id !== 'string') return null;
  const cols = known.map(([, c]) => c);
  const values = known.map(([k, , codec]) => codec.toDb(fields[k as string]));
  const updates = cols.filter((c) => c !== 'id').map((c) => `${c} = excluded.${c}`);
  return {
    sql: `INSERT INTO ${spec.table} (${[...cols, 'updated_hlc'].join(', ')}) VALUES (${[...cols, 'updated_hlc'].map(() => '?').join(', ')})
          ON CONFLICT (id) DO UPDATE SET ${[...updates, 'updated_hlc = excluded.updated_hlc'].join(', ')}`,
    params: [...values, updatedHlc],
  };
}
